import { KeywordService } from "@/services/admin-services";
import { Keyword } from "@/services/admin-services/models/Keyword";
import { Language } from "@/services/global-services/models";
import { PencilIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Box, Button, Chip, Grid, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import ConfirmDeleteDialog from "./components/ConfirmDeleteDialog";
import CreateDialog from "./components/CreateDialog/CreateDialog";

type KeywordEditorProps = {
    id: string;
    languages: Language[];
};

const KeywordEditor = ({ id, languages }: KeywordEditorProps) => {
    const [keywords, setKeywords] = useState<Keyword[]>([]);
    const [activeKeyword, setActiveKeyword] = useState<Keyword | null>();
    const [confirmDelete, setConfirmDelete] = useState<Keyword | null>(null);
    const [confirmDeleteCode, setConfirmDeleteCode] = useState<string | null>(null);

    const theme = useTheme();

    const keywordService = useMemo(() => new KeywordService("int"), []);

    useEffect(() => {
        keywordService.get(id).then(setKeywords);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onCreate = () => setActiveKeyword({ id: "", labels: [] });
    const onEdit = (keyword: Keyword) => setActiveKeyword(keyword);

    const onDelete = (keyword: Keyword, code: string) => {
        setConfirmDelete(keyword);
        setConfirmDeleteCode(code);
    };

    const onCancelDelete = () => {
        setConfirmDelete(null);
        setConfirmDeleteCode(null);
    };

    const onConfirmDelete = (success: boolean) => {
        if (!success) return;

        const newKeywords = keywords.filter((x) => x.id !== confirmDelete!.id);
        setKeywords(newKeywords);
        onCancelDelete();
    };

    const onCloseDialog = () => setActiveKeyword(null);
    const onSubmitDialog = (keyword: Keyword) => {
        const newKeywords = [...keywords];
        const existingKeyword = newKeywords.find((x) => x.id === keyword.id);
        if (existingKeyword) {
            existingKeyword.labels = keyword.labels;
        } else {
            newKeywords.push(keyword);
        }

        setKeywords(newKeywords);

        onCancelDelete();
        onCloseDialog();
    };

    return (
        <Grid container spacing={2} p={{ xs: theme.spacing(0, 2), md: theme.spacing(0, 4) }}>
            <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight={700}>
                    Keywords
                </Typography>
                <Typography variant="subtitle2" color={"text.secondary"}>
                    Manage the keywords for this element.
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
                <Grid container spacing={4}>
                    {languages.map((language, index) => (
                        <Grid size={{ xs: 12, xl: 6 }} key={index}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                {language.label}
                            </Typography>
                            <Box sx={{ margin: theme.spacing(0, -0.5) }}>
                                {keywords.map((keyword, index) => {
                                    const keywordLabel = keyword.labels.find((x) => x.languageCode === language.slug);
                                    if (!keywordLabel) return null;

                                    return (
                                        <Chip
                                            key={index}
                                            icon={<PencilIcon className="h-5 w-5 pl-0.5" />}
                                            label={keywordLabel.label}
                                            onClick={() => onEdit(keyword)}
                                            onDelete={() => onDelete(keyword, language.slug)}
                                            sx={{ m: 0.5 }}
                                        />
                                    );
                                })}
                            </Box>
                        </Grid>
                    ))}
                    <Grid size={{ xs: 12 }} sx={{ display: "flex" }}>
                        <Button
                            onClick={() => onCreate()}
                            variant="contained"
                            startIcon={<PlusIcon className="h-5 w-5" />}
                            sx={{ ml: "auto" }}
                        >
                            Add Keyword
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            {activeKeyword && (
                <CreateDialog
                    open={Boolean(activeKeyword)}
                    element={id}
                    keyword={activeKeyword}
                    languages={languages}
                    onClose={onCloseDialog}
                    onSubmit={onSubmitDialog}
                />
            )}

            {confirmDelete && confirmDeleteCode && (
                <ConfirmDeleteDialog
                    open={Boolean(confirmDelete)}
                    element={id}
                    keyword={confirmDelete}
                    code={confirmDeleteCode}
                    onCancel={onCancelDelete}
                    onDelete={onConfirmDelete}
                    onUpdate={onSubmitDialog}
                />
            )}
        </Grid>
    );
};

export default KeywordEditor;
